import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AUTH_STATUS } from "./constants";

import {
    clearAuthSession,
    ensureAuthSession,
} from "@/entities/localstorage/model/lib/authSessionStorage";
import { refreshToken } from "@/entities/session/api/refreshToken";
import { useGetUserQuery } from "@/entities/session/api/useGetUserQuery";
import { useSessionStore } from "@/entities/session/models/store/useSessionStore";
import type { User } from "@/entities/session/models/types/storeType";
import { getAccessToken, setAccessToken } from "@/shared/config/orval/makeRequest";
import { getRefreshToken } from "@/shared/lib/getRefreshToken";
import { useCentrifugeConnect } from "@/shared/lib/useCentrifugeConnect";
import { Loader } from "@/shared/ui/Loader/Loader";

export const SessionProvider = ({ children }: { children: React.ReactNode }) => {
    const { setUserInfo, setAccountInfo } = useSessionStore();
    const navigate = useNavigate();
    const [authStatus, setAuthStatus] = useState<(typeof AUTH_STATUS)[keyof typeof AUTH_STATUS]>(
        AUTH_STATUS.PENDING,
    );
    const websocketToken = authStatus === AUTH_STATUS.AUTHENTICATED ? getAccessToken() : "";

    useCentrifugeConnect(websocketToken);

    useEffect(() => {
        const checkAuth = async () => {
            const accessToken = getAccessToken();
            if (accessToken) {
                ensureAuthSession();
                setAuthStatus(AUTH_STATUS.AUTHENTICATED);
                return;
            }

            const refreshTokenValue = getRefreshToken();
            if (refreshTokenValue) {
                try {
                    await refreshToken();
                    ensureAuthSession();
                    setAuthStatus(AUTH_STATUS.AUTHENTICATED);
                } catch {
                    clearAuthSession();
                    localStorage.removeItem("refreshToken");
                    setAuthStatus(AUTH_STATUS.UNAUTHENTICATED);
                }
            } else {
                setAuthStatus(AUTH_STATUS.UNAUTHENTICATED);
            }
        };

        checkAuth();
    }, []);

    useEffect(() => {
        const handleStorage = (event: StorageEvent) => {
            if (event.key !== "refreshToken" || event.newValue) return;

            setAccessToken("");
            clearAuthSession();
            setAuthStatus(AUTH_STATUS.UNAUTHENTICATED);
        };

        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    const { data, isLoading, error } = useGetUserQuery(authStatus === AUTH_STATUS.AUTHENTICATED);

    useEffect(() => {
        if (data && !error) {
            setAccountInfo(data);
        }

        if (error) {
            navigate("/login", { replace: true });
            return;
        }
    }, [data, error]);

    useEffect(() => {
        if (authStatus === AUTH_STATUS.UNAUTHENTICATED) {
            navigate("/login", { replace: true });
            return;
        }

        if (authStatus === AUTH_STATUS.AUTHENTICATED) {
            try {
                const user = jwtDecode<User>(getAccessToken());
                setUserInfo(user);
            } catch {
                navigate("/login", { replace: true });
            }
        }
    }, [authStatus]);

    if (isLoading || authStatus === AUTH_STATUS.PENDING) {
        return (
            <div
                style={{
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Loader />
            </div>
        );
    }

    return <div style={{ height: "100%" }}>{children}</div>;
};

export default SessionProvider;
